import backendCall from '@/services/AxiosService';

export interface BuildPositionResponse {
  position_id: number;
  build_id: number;
  unit_number: string;
  car_number: string;
  well: string;
  position: string;
  sequence: number;
  photo_url: string | null;
  comments?: string | null;
  temp_c?: number | null;
  running_temp_c?: number | null;
  billed_setpoint?: number | null;
  actual_setpoint?: number | null;
  out_of_range_flag?: boolean;
  setpoint_discrepancy_flag?: boolean;
  created_at: string;
  created_by: number | null;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
}

export const fetchBuildPositions = async (buildId: number) => {
  const response = await backendCall.get<{ success: boolean; data: BuildPositionResponse[] }>(
    `power-pack/builds/${buildId}/positions`
  );
  return response.data.data;
};

export const createBuildPosition = async (
  buildId: number,
  payload: {
    unitNumber: string;
    carNumber: string;
    well: string;
    position: string;
    photoUrl?: string | null;
    comments?: string | null;
    tempC?: number | null;
    runningTempC?: number | null;
    billedSetpoint?: number | null;
    actualSetpoint?: number | null;
  }
) => {
  const response = await backendCall.post<{ success: boolean; data: BuildPositionResponse }>(
    `power-pack/builds/${buildId}/positions`,
    payload
  );
  return response.data.data;
};

export const createBuildPositionsBulk = async (
  buildId: number,
  positions: Array<{
    unitNumber: string;
    carNumber: string;
    well: string;
    position: string;
    photoUrl?: string | null;
  }>
) => {
  const response = await backendCall.post<{
    success: boolean;
    data: BuildPositionResponse[];
  }>(`power-pack/builds/${buildId}/positions/bulk`, { positions });
  return response.data.data;
};

export const deleteBuildPosition = async (buildId: string | number, positionId: string | number) => {
  await backendCall.delete(`power-pack/builds/${buildId}/positions/${positionId}`);
};

export const updateBuildRailcar = async (buildId: number, railcar: string) => {
  const response = await backendCall.patch<{
    success: boolean;
    data: {
      build_id: number;
      railcar: string;
    };
  }>(`power-pack/builds/${buildId}/railcar`, { railcar });
  return response.data.data;
};

export const updateBuildCablesHooks = async (
  buildId: number,
  numCables: number | null,
  numHooks: number | null
) => {
  const response = await backendCall.patch<{
    success: boolean;
    data: {
      build_id: number;
      num_cables: number | null;
      num_hooks: number | null;
    };
  }>(`power-pack/builds/${buildId}/cables-hooks`, { numCables, numHooks });
  return response.data.data;
};

export const updateBuildFuelEngineHours = async (
  buildId: number,
  fuelLevel: string | null,
  g1Hours: number | null,
  g2Hours: number | null
) => {
  const response = await backendCall.patch<{
    success: boolean;
    data: {
      build_id: number;
      fuel_level: string | null;
      g1_hours: number | null;
      g2_hours: number | null;
    };
  }>(`power-pack/builds/${buildId}/fuel-engine-hours`, { fuelLevel, g1Hours, g2Hours });
  return response.data.data;
};

export const updateBuildTimesNotes = async (
  buildId: number,
  endTime: string | null,
  delays: string | null,
  repairsRequired: string | null
) => {
  const response = await backendCall.patch<{
    success: boolean;
    data: {
      build_id: number;
      end_time: string | null;
      delays: string | null;
      repairs_required: string | null;
    };
  }>(`power-pack/builds/${buildId}/times-notes`, { endTime, delays, repairsRequired });
  return response.data.data;
};

export const completeBuild = async (buildId: number) => {
  const response = await backendCall.put<{
    success: boolean;
    data: {
      completed_at: string;
      completed_by: number;
      report_sent_at: string;
    };
  }>(`power-pack/builds/${buildId}/complete`);
  return response.data.data;
};

export const reorderBuildPositions = async (
  buildId: number,
  positions: Array<{ position_id: number; sequence: number }>
) => {
  const response = await backendCall.patch<{
    success: boolean;
    message: string;
  }>(`power-pack/builds/${buildId}/positions/reorder`, { positions });
  return response.data;
};

/**
 * Download build report PDF in specified format
 * @param buildId - The build ID
 * @param format - 'new' or 'original'
 */
export const downloadBuildReport = async (buildId: number, format: 'new' | 'original') => {
  const endpoint = format === 'new'
    ? `power-pack/builds/${buildId}/build-report`
    : `power-pack/builds/${buildId}/build-report-original`;

  const response = await backendCall.get(endpoint, {
    responseType: 'blob'
  });

  // Create download link and trigger download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Build-${buildId}-${format}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Fetch unit numbers from the last 2 completed yard checks
 * @returns Array of unit numbers
 */
export const fetchRecentYardCheckUnits = async (): Promise<string[]> => {
  const response = await backendCall.get<{ success: boolean; data: string[] }>(
    'power-pack/yard-check-units'
  );
  return response.data.data;
};
